import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import { FulfillmentAdminClient } from '../../../infrastructure/FulfillmentAdminClient';
import { ROUTES } from '../../../routes/routes';
import { serverRefusal } from '../../../utils/fulfillmentErrors';
import { asciiScanField } from '@/pages/fulfillment/components/asciiScanField';
import { CameraScanField } from '@/pages/fulfillment/components/CameraScanField';

import { StockClient } from '@/infrastructure/StockClient';
import { useStockSkuValidation } from '@/pages/fulfillment/inventory/stockSkuValidation';

const MOVE_FAILED = 'The stock move failed.';

export default function FulfillmentStockMovePage() {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const [sku, setSku] = useState('');
  const stockClient = useMemo(() => new StockClient(), []);
  const skuValidation = useStockSkuValidation(stockClient, sku);
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [quantity, setQuantity] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<'success' | 'danger' | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const pendingRequestRef = useRef<{ fingerprint: string; idempotencyKey: string } | null>(null);

  const valid =
    skuValidation.valid &&
    fromLocation.trim() !== '' &&
    toLocation.trim() !== '' &&
    fromLocation.trim() !== toLocation.trim() &&
    Number.isInteger(Number(quantity)) &&
    Number(quantity) > 0;

  const submit = async () => {
    if (!valid || busy) {
      return;
    }
    setBusy(true);
    setResult(null);
    setRefusal(null);
    const requestBody = {
      sku: sku.trim(),
      fromLocation: fromLocation.trim(),
      toLocation: toLocation.trim(),
      qty: Number(quantity),
    };
    const fingerprint = JSON.stringify(requestBody);
    if (pendingRequestRef.current?.fingerprint !== fingerprint) {
      pendingRequestRef.current = { fingerprint, idempotencyKey: uuidv4() };
    }
    try {
      const response = await client.moveStock({
        ...requestBody,
        idempotencyKey: pendingRequestRef.current.idempotencyKey,
      });
      pendingRequestRef.current = null;
      setResult(response.moved ? 'success' : 'danger');
    } catch (err) {
      // A virtual-location refusal and a network outage are different problems; only one names itself.
      setRefusal(serverRefusal(err) ?? null);
      setResult('danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container fluid className="py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2 className="mb-1">Stock Move</h2>
          <div className="text-muted small">Move quantity atomically from one shelf to another.</div>
        </div>
        <Link to={ROUTES.fulfillment.inventory} className="btn btn-outline-secondary btn-sm">
          Back to inventory
        </Link>
      </div>
      {result && (
        <Alert variant={result}>{result === 'success' ? 'The stock move is done.' : (refusal ?? MOVE_FAILED)}</Alert>
      )}
      <Card>
        <Card.Body>
          <Row className="g-3">
            <Col md={2}>
              <Form.Group controlId="move-sku">
                <Form.Label>SKU</Form.Label>
                <CameraScanField label="Scan SKU with camera">
                  <Form.Control
                    value={sku}
                    onChange={(event) => setSku(event.target.value)}
                    aria-describedby="move-sku-validation"
                    isInvalid={skuValidation.invalid}
                  />
                </CameraScanField>
                <div id="move-sku-validation" className="small" aria-live="polite">
                  {skuValidation.message}
                  {skuValidation.canRetry && (
                    <button type="button" onClick={skuValidation.retry}>
                      Retry SKU lookup
                    </button>
                  )}
                </div>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group controlId="move-from">
                <Form.Label>From location</Form.Label>
                <CameraScanField label="Scan from location with camera">
                  <Form.Control value={fromLocation} {...asciiScanField(setFromLocation)} />
                </CameraScanField>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group controlId="move-to">
                <Form.Label>To location</Form.Label>
                <CameraScanField label="Scan to location with camera">
                  <Form.Control value={toLocation} {...asciiScanField(setToLocation)} />
                </CameraScanField>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group controlId="move-qty">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  value={quantity}
                  onChange={(event) => setQuantity(event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button disabled={!valid || busy} onClick={() => void submit()}>
                Move stock
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
}
